import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { useTranslation, Trans } from 'react-i18next';

import { SectionWrapper } from '../hoc';
import { slideIn, textVariant } from '../utils/motion';
import { styles } from '../styles';
import {
  EmailIcon,
  GitHubIcon,
  LinkedInIcon,
  LocationIcon,
  DownloadIcon,
} from './icons/ContactIcons.jsx';
import { contactData } from '../data/index.js';

const Contact = () => {
  const { t } = useTranslation();
  const formRef = useRef();
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.message) {
      alert(t('contact.alert.fill_fields'));
      return;
    }

    setLoading(true);

    emailjs
      .send(
        import.meta.env.VITE_APP_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          to_name: 'Luka Vukanović',
          from_email: form.email,
          to_email: 'l.vukanovic32@gmail.com',
          message:
            'Name: ' +
            form.name +
            '\n' +
            'Email: ' +
            form.email +
            '\n' +
            'Content:\n' +
            form.message,
        },
        import.meta.env.VITE_APP_EMAILJS_PUBLIC_KEY
      )
      .then(
        () => {
          setLoading(false);
          alert(t('contact.alert.success'));
          setForm({ name: '', email: '', message: '' });
        },
        (error) => {
          setLoading(false);
          console.error(error);
          alert(t('contact.alert.error'));
        }
      );
  };

  return (
    <div className="px-6 sm:px-10">
      <motion.div variants={textVariant()} className="text-center">
        <h3 className={styles.sectionText}>{t('contact.title')}</h3>
      </motion.div>

      <div className="mt-12 min-[1050px]:flex-row flex-col flex min-[1050px]:gap-24 gap-12 overflow-hidden">
        <motion.div
          variants={slideIn('left', 'tween', 0.2, 1)}
          className="w-full min-[1050px]:w-1/2 bg-tertiary p-8 rounded-2xl flex flex-col"
        >
          <div className="flex-grow">
            <p className="text-white font-black md:text-[36px] sm:text-[30px] text-[26px]">
              {t('contact.heading')}
            </p>
            <p className="text-secondary mt-4">{t('contact.subheading')}</p>

            <p className="text-secondary mt-8 text-[15px] leading-relaxed">
              <Trans
                i18nKey="contact.ps_content"
                components={[
                  <span className="text-white font-medium" />,
                  <span />,
                  <span className="text-white font-medium" />,
                  <span />,
                  <span className="text-white font-medium" />,
                  <span />,
                  <span className="text-white font-medium" />,
                  <span />,
                  <span className="text-white font-medium" />,
                  <span />,
                  <span className="text-white font-medium" />,
                ]}
              />
            </p>

            <div className="mt-8 flex flex-col gap-6">
              <a
                href={`mailto:${contactData.YOUR_EMAIL}`}
                className="flex items-center gap-4
                            text-secondary hover:text-white transition-colors"
              >
                <div className="w-6 h-6 flex-shrink-0">
                  <EmailIcon />
                </div>
                <span>{contactData.YOUR_EMAIL}</span>
              </a>
              <div className="flex items-center gap-4 text-secondary">
                <div className="w-6 h-6 flex-shrink-0">
                  <LocationIcon />
                </div>
                <span>{t('contact.location')}</span>
              </div>
            </div>
            <a
              href={contactData.CV_URL}
              download="Luka_Vukanovic-CV.pdf"
              className="bg-gradient-to-br from-blue-600 to-cyan-400 py-3 px-6 outline-none w-fit
                           text-white font-bold shadow-md shadow-primary rounded-xl mt-10 mb-8 flex items-center
                           gap-3 hover:scale-105 transition-transform"
              aria-label="Download CV"
            >
              <div className="w-5 h-5">
                <DownloadIcon />
              </div>
              <span>Download CV</span>
            </a>
          </div>
          <div className="flex gap-6 mt-auto pt-6 border-t border-slate-700 items-center">
            <p className="text-secondary text-sm font-semibold">
              {t('contact.find_me')}
            </p>
            <a
              href={contactData.GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10
                        text-secondary hover:text-white transition-colors"
              aria-label="GitHub Profile"
            >
              <GitHubIcon />
            </a>
            <a
              href={contactData.LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10
                        text-secondary hover:text-white transition-colors"
              aria-label="LinkedIn Profile"
            >
              <LinkedInIcon />
            </a>
          </div>
        </motion.div>

        <motion.div
          variants={slideIn('right', 'tween', 0.2, 1)}
          className="w-full min-[1050px]:w-1/2"
        >
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="flex flex-col gap-6 h-full"
          >
            <label className="flex flex-col">
              <span className="text-white font-medium mb-4">
                {t('contact.form.name_label')}
              </span>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={t('contact.form.name_placeholder')}
                className="bg-tertiary py-4 px-6 placeholder:text-secondary text-white rounded-lg outline-none border-none font-medium"
              />
            </label>
            <label className="flex flex-col">
              <span className="text-white font-medium mb-4">
                {t('contact.form.email_label')}
              </span>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder={t('contact.form.email_placeholder')}
                className="bg-tertiary py-4 px-6 placeholder:text-secondary text-white rounded-lg outline-none border-none font-medium"
              />
            </label>
            <label className="flex flex-col flex-grow">
              <span className="text-white font-medium mb-4">
                {t('contact.form.message_label')}
              </span>
              <textarea
                rows="7"
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder={t('contact.form.message_placeholder')}
                className="bg-tertiary py-4 px-6 placeholder:text-secondary text-white rounded-lg outline-none border-none font-medium h-full resize-none"
              />
            </label>
            <button
              type="submit"
              className="bg-gradient-to-br from-blue-600 to-cyan-400 py-3 px-8 outline-none w-fit text-white font-bold shadow-md shadow-primary rounded-xl self-center min-[1050px]:self-end hover:scale-105 transition-transform"
            >
              {loading
                ? t('contact.form.sending_button')
                : t('contact.form.send_button')}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default SectionWrapper(Contact, 'contact');
